import { UUID } from 'crypto';
import { createStowagePlanChange } from '../StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { createSequenceChangeValue } from './SequenceChangeUtil';
import { SequenceChangeType } from 'src/types/ViewModel/Stowage/Rotation/SequenceChangeType';

export const createCargoOperationChangeData = (cargoOperation: RotationCargoOperation): Partial<RotationCargoOperation> => {
    return {
        portCallGuid: cargoOperation.portCallGuid,
        berthCallGuid: cargoOperation.berthCallGuid,
        cargoGuid: cargoOperation.cargoGuid,
        operationType: cargoOperation.operationType,
        cargoOperationSequenceNumber: cargoOperation.cargoOperationSequenceNumber,
        contractWeight: cargoOperation.contractWeight,
        nominatedWeight: cargoOperation.nominatedWeight,
        billOfLadingWeight: cargoOperation.billOfLadingWeight,
        imosCargoOperationId: cargoOperation.imosCargoOperationId,
        imosCargoId: cargoOperation.imosCargoId,
        dataSource: cargoOperation.dataSource,
        demurrageRatePerDay: cargoOperation.demurrageRatePerDay,
        pumpingRatePerHour: cargoOperation.pumpingRatePerHour,
        cpDate: cargoOperation.cpDate,
        hasChangedSequence: cargoOperation.hasChangedSequence,
        eventOrderOverride: cargoOperation.eventOrderOverride,
    };
};

/**
 * Creates a SequenceChangeType object with the original sequence number
 *
 * @param originalNumber The original sequence number to store
 * @returns A SequenceChangeType object
 */
const createSequenceChange = (originalNumber: number): SequenceChangeType => {
    return {
        originalSequenceNumber: originalNumber,
    };
};

/**
 * Creates a cargo operation order change tracking object with enhanced sequence handling.
 * Properly handles operations returning to their original position.
 *
 * @param stowagePlanGuid The GUID of the stowage plan being modified
 * @param changeDataGuid The GUID of the cargo operation data being modified
 * @param cargoOperationSequenceNumber The new sequence number for the cargo operation
 * @param cargoOperationData Optional cargo operation data containing current state
 * @param relatedOperations Optional array of related operations affected by this change
 * @returns A StowagePlanChange object representing the sequence change
 */
export const createCargoOperationOrderChange = (
    stowagePlanGuid: UUID,
    changeDataGuid: UUID,
    cargoOperationSequenceNumber: number,
    cargoOperationData?: RotationCargoOperation,
    relatedOperations?: RotationCargoOperation[]
) => {
    let sequenceChangeInfo: SequenceChangeType | null = null;
    const affectedOperations: Record<string, number> = {};

    // Check for explicit null hasChangedSequence value which indicates a reset to original position
    const isReturningToOriginal = cargoOperationData && cargoOperationData.hasChangedSequence === null;

    // If we have the current cargo operation data, use advanced sequence tracking
    if (cargoOperationData) {
        if (isReturningToOriginal) {
            // The operation is returning to its original position
            sequenceChangeInfo = null;
        } else {
            // Use the base sequence tracking logic from createSequenceChangeValue
            const result = createSequenceChangeValue(cargoOperationSequenceNumber, cargoOperationData, 'cargoOperationSequenceNumber');

            // Ensure we always use a SequenceChangeType object (or null)
            if (result === true) {
                // If it's just a boolean true, convert it to a proper SequenceChangeType
                // by using the current sequence number (before change) as the original
                sequenceChangeInfo = createSequenceChange(cargoOperationData.cargoOperationSequenceNumber);
            } else if (result === false || result === null) {
                sequenceChangeInfo = null;
            } else {
                // It's already a SequenceChangeType object
                sequenceChangeInfo = result;
            }
        }

        // If we have related operations that are affected by this change, record their state
        if (relatedOperations && relatedOperations.length > 0) {
            relatedOperations.forEach((operation) => {
                if (operation.cargoOperationGuid) {
                    affectedOperations[operation.cargoOperationGuid] = operation.cargoOperationSequenceNumber;
                }
            });
        }
    } else if (!isReturningToOriginal) {
        // If we don't have current data but it's not returning to original,
        // we still need to mark it as changed with a placeholder
        sequenceChangeInfo = createSequenceChange(cargoOperationSequenceNumber);
    }

    // Return a stowage plan change with enhanced data
    return createStowagePlanChange(stowagePlanGuid, changeDataGuid, StowageChangeDataType.RotationCargoOperation, {
        cargoOperationSequenceNumber,
        hasChangedSequence: sequenceChangeInfo,
        affectedOperations: Object.keys(affectedOperations).length > 0 ? affectedOperations : undefined,
        restoredToOriginal: isReturningToOriginal ? true : undefined,
    });
};
